import React from "react";
import { Field } from "redux-form";
import ReduxFormTextField from "../../../common/reduxFormFields/ReduxFormTextField";
import ReduxFormcheckboxField from "../../../common/reduxFormFields/ReduxFormCheckboxField";
import TextField from "../../../common/formFields/TextField";
import CheckboxField from "../../../common/formFields/CheckboxField";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import { DISABILITY_OR_SOCIAL_SECURITY } from "../../../models/requestType";
import { RequestorFormConstants } from "./DataEntryFieldsConstants";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm } from "./formFieldHelpers";

/**
 * DDS (Disability / Social Security, Request Type 31) barcode-sourced field set
 * (Spec #35136 / F1f). Extracted from RequestorContactPanel.tsx's inline DDS
 * block following the F1a per-field-extraction precedent (CaseNumberField.tsx
 * et al.): the SAME six barcode fields + "No Barcode" checkbox render in both
 * Classic Mode (`ClassicDdsFields`, still bound via redux-form <Field> context,
 * byte-identical to the pre-extraction inline block) and Quick Entry (the
 * default export, a controlled plain-input row that writes DATA_ENTRY_FIELDS_FORM
 * via the shared writeFieldToForm helper, exactly like the other Quick Entry rows).
 *
 * No PHI is logged here - SSN and the other DDS identifiers are only written to
 * the form, never logged (see .claude/rules/hipaa-compliance.md).
 */

export const DDS_INFO_ROW_ID = "ddsInfo";

/** The six barcode-sourced DDS fields, in render order (RQID first). */
export const DDS_BARCODE_FIELDS: readonly string[] = [
    RequestorFormConstants.RQID,
    RequestorFormConstants.TX_SITE_CODE,
    RequestorFormConstants.DOC_RETURN_CODE,
    RequestorFormConstants.SSN,
    RequestorFormConstants.DOC_TYPE,
    RequestorFormConstants.ROUTING_FIELD,
];

/**
 * Doc Type value Classic defaults when "No Barcode" is UNchecked (re-enabling the
 * fields). Mirrors Classic's onChangeNoBarcode default (DataEntryDetailsRightPane).
 */
export const DDS_DEFAULT_DOC_TYPE = "0001";

/**
 * Caps, checked against the columns they land in - `dbo.tblRequestAuthQuestion` (AB#35251):
 *   RQID varchar(50), SiteCode varchar(10), DocReturnCode varchar(10),
 *   RoutingField varchar(10), DocType varchar(10), SSN varchar(25).
 *
 * Doc Return Code and Routing Field were 50 - five times their column width - so the field accepted
 * input the database then truncates or rejects on save, which is the exact defect AB#35251 exists to
 * remove. Both are now 10.
 *
 * SSN (9) and Doc Type (4) are deliberately TIGHTER than their columns: 9 is a complete SSN with no
 * separators, and 4 is the width of the "0001" doc-type code Classic defaults. A cap may be stricter
 * than the column; it must never be looser.
 */
const DDS_FIELD_MAX_LENGTHS: Readonly<Record<string, number>> = {
    [RequestorFormConstants.RQID]: 50,
    [RequestorFormConstants.TX_SITE_CODE]: 10,
    [RequestorFormConstants.DOC_RETURN_CODE]: 10,
    [RequestorFormConstants.SSN]: 9,
    [RequestorFormConstants.DOC_TYPE]: 4,
    [RequestorFormConstants.ROUTING_FIELD]: 10,
};

/** Exported so the row's inline DDS search can cap its Site Code box from the same source. */
export const DDS_SITE_CODE_MAX_LENGTH = DDS_FIELD_MAX_LENGTHS[RequestorFormConstants.TX_SITE_CODE];

// ---------------------------------------------------------------------------
// Classic Mode variant (redux-form <Field> block, extracted verbatim from
// RequestorContactPanel.tsx lines 204-323 - tidy-first structural move, no
// behavior change). The host still owns/threads every change/blur handler and
// the required-vs-No-Barcode computation, exactly as before.
// ---------------------------------------------------------------------------
/**
 * redux-form field change/blur handler. redux-form passes (event, newValue,
 * previousValue, name) - callers here don't rely on a fixed arity, so a variadic
 * void callback is the honest shared type (was `any`, S3).
 */
type ReduxFormFieldHandler = (...args: any[]) => void;

export interface ClassicDdsFieldsProps {
    /** redux-form state slice for the host form - only `.values` is read here. */
    readonly formState: { readonly values: Record<string, any> };
    readonly isDisableDDSField: boolean;
    readonly drErrorMessage?: string;
    readonly docTypeErrorMessage?: string;
    readonly rfErrorMessage?: string;
    readonly onChangeRQID?: ReduxFormFieldHandler;
    readonly onBlurRQID?: ReduxFormFieldHandler;
    readonly onChangeTXSiteCode?: ReduxFormFieldHandler;
    readonly onBlurTXSiteCode?: ReduxFormFieldHandler;
    readonly onChangeDR?: ReduxFormFieldHandler;
    readonly onBlurDR?: ReduxFormFieldHandler;
    readonly onChangeSSN?: ReduxFormFieldHandler;
    readonly onBlurSSN?: ReduxFormFieldHandler;
    readonly onChangeDocType?: ReduxFormFieldHandler;
    readonly onBlurDocType?: ReduxFormFieldHandler;
    readonly onChangeRF?: ReduxFormFieldHandler;
    readonly onBlurRF?: ReduxFormFieldHandler;
    readonly onChangeNoBarcode?: ReduxFormFieldHandler;
}

/** Required unless "No Barcode" is explicitly checked - the isBarcodeChecked quirk: undefined => still required. */
const classicDdsRequired = (formState: { readonly values: Record<string, any> }): boolean =>
    formState.values[RequestorFormConstants.NO_BARCODE] !== undefined
        ? !formState.values[RequestorFormConstants.NO_BARCODE]
        : true;

export const ClassicDdsFields: React.FC<ClassicDdsFieldsProps> = (props) => {
    const { isDisableDDSField } = props;
    const required = classicDdsRequired(props.formState);
    return (
        <>
            <div style={{ marginLeft: "0", marginTop: 12 }} className="row dds-row-1">
                <div style={{ flexBasis: "33%" }}>
                    <Field
                        name={RequestorFormConstants.RQID}
                        component={ReduxFormTextField}
                        props={{
                            label: "RQID",
                            placeholder: "Enter RQID",
                            maxLength: 50,
                            onChange: props.onChangeRQID,
                            required,
                            errorMessage: "",
                            onBlur: props.onBlurRQID,
                            disabled: isDisableDDSField,
                            suppressEnterSubmit: true
                        }}
                    />
                </div>
                <div style={{ flexBasis: "33%" }}>
                    <Field
                        name={RequestorFormConstants.TX_SITE_CODE}
                        component={ReduxFormTextField}
                        props={{
                            label: "TX Site Code",
                            placeholder: "Site Code",
                            maxLength: 10,
                            onChange: props.onChangeTXSiteCode,
                            required,
                            errorMessage: "",
                            onBlur: props.onBlurTXSiteCode,
                            disabled: isDisableDDSField,
                            suppressEnterSubmit: true
                        }}
                    />
                </div>
                <div style={{ flexBasis: "33%" }}>
                    <Field
                        name={RequestorFormConstants.DOC_RETURN_CODE}
                        component={ReduxFormTextField}
                        props={{
                            label: "Doc Return Code",
                            placeholder: "Doc Return Code",
                            maxLength: 50,
                            onChange: props.onChangeDR,
                            required,
                            errorMessage: props.drErrorMessage,
                            onBlur: props.onBlurDR,
                            disabled: isDisableDDSField,
                            suppressEnterSubmit: true
                        }}
                    />
                </div>
            </div>
            <div style={{ marginLeft: "0" }} className="row dds-row-2">
                <div style={{ flexBasis: "33%" }}>
                    <Field
                        name={RequestorFormConstants.SSN}
                        component={ReduxFormTextField}
                        props={{
                            label: "SSN",
                            placeholder: "SSN",
                            maxLength: 9,
                            onChange: props.onChangeSSN,
                            required,
                            errorMessage: "",
                            onBlur: props.onBlurSSN,
                            disabled: isDisableDDSField,
                            suppressEnterSubmit: true
                        }}
                    />
                </div>
                <div style={{ flexBasis: "33%" }}>
                    <Field
                        name={RequestorFormConstants.DOC_TYPE}
                        component={ReduxFormTextField}
                        props={{
                            label: "Doc Type",
                            placeholder: "Doc Type",
                            maxLength: 4,
                            onChange: props.onChangeDocType,
                            required,
                            errorMessage: props.docTypeErrorMessage,
                            onBlur: props.onBlurDocType,
                            disabled: isDisableDDSField,
                            suppressEnterSubmit: true
                        }}
                    />
                </div>
                <div style={{ flexBasis: "33%" }}>
                    <Field
                        name={RequestorFormConstants.ROUTING_FIELD}
                        component={ReduxFormTextField}
                        props={{
                            label: "Routing Field",
                            placeholder: "Routing Field",
                            maxLength: 50,
                            onChange: props.onChangeRF,
                            required,
                            errorMessage: props.rfErrorMessage,
                            onBlur: props.onBlurRF,
                            disabled: isDisableDDSField,
                            suppressEnterSubmit: true
                        }}
                    />
                </div>
            </div>
            <div className="row no-barcode">
                <div className="select-all-check">
                    <Field
                        name={RequestorFormConstants.NO_BARCODE}
                        component={ReduxFormcheckboxField}
                        props={{
                            fieldlabel: "",
                            checkboxLabel: 'No Barcode',
                            onChange: props.onChangeNoBarcode,
                        }}
                    />
                </div>
            </div>
        </>
    );
};

// ---------------------------------------------------------------------------
// Quick Entry variant (Spec #35136 / F1f). Controlled plain inputs that write
// DATA_ENTRY_FIELDS_FORM via writeFieldToForm - the same mechanism every other
// Quick Entry row uses (CaseNumberField et al.). No redux-form <Field>, no
// per-field meta (Quick Entry expresses required-ness through the row's own
// FieldGroupRowStatus, not inline redux-form errors).
// ---------------------------------------------------------------------------
export interface DdsFieldsGroupProps {
    /** Current DATA_ENTRY_FIELDS_FORM values (this.props.formState.values). */
    readonly values: Record<string, any>;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Fires on every DDS field / No Barcode change so the Quick Entry container
     * can mark the row as actively-being-edited (same role CaseNumberField's
     * onCaseNumberChange plays), keeping the row from swapping out mid-edit.
     */
    readonly onDdsFieldChange?: () => void;
}

const DDS_FIELD_LABELS: Readonly<Record<string, string>> = {
    [RequestorFormConstants.RQID]: "RQID",
    [RequestorFormConstants.TX_SITE_CODE]: "TX Site Code",
    [RequestorFormConstants.DOC_RETURN_CODE]: "Doc Return Code",
    [RequestorFormConstants.SSN]: "SSN",
    [RequestorFormConstants.DOC_TYPE]: "Doc Type",
    [RequestorFormConstants.ROUTING_FIELD]: "Routing Field",
};

const DdsFieldsGroup: React.FC<DdsFieldsGroupProps> = (props) => {
    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;
    const noBarcode = Boolean(props.values[RequestorFormConstants.NO_BARCODE]);

    const onChangeField = (field: string) => (value: string) => {
        writeFieldToForm(props.dispatch, field, value, formName);
        props.onDdsFieldChange?.();
    };

    // Mirrors Classic's onChangeNoBarcode (DataEntryDetailsRightPane.tsx:1072-1105)
    // MINUS the immediate PATCH/billing side effects, which Quick Entry defers to
    // its Confirm-All model like every other row: check clears + disables the six
    // fields; uncheck re-enables them and defaults Doc Type to "0001".
    const onChangeNoBarcode = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        writeFieldToForm(props.dispatch, RequestorFormConstants.NO_BARCODE, checked, formName);
        if (checked) {
            DDS_BARCODE_FIELDS.forEach(field => writeFieldToForm(props.dispatch, field, "", formName));
            writeFieldToForm(props.dispatch, RequestorFormConstants.RQ_SITE_CODE, "", formName);
        } else {
            writeFieldToForm(props.dispatch, RequestorFormConstants.DOC_TYPE, DDS_DEFAULT_DOC_TYPE, formName);
        }
        props.onDdsFieldChange?.();
    };

    return (
        <div className="qe-dds-fields">
            {DDS_BARCODE_FIELDS.map(field => (
                <TextField
                    key={field}
                    label={DDS_FIELD_LABELS[field]}
                    maxLength={DDS_FIELD_MAX_LENGTHS[field]}
                    value={props.values[field] ?? ""}
                    disabled={noBarcode}
                    onChange={onChangeField(field)}
                    suppressEnterSubmit
                />
            ))}
            <CheckboxField
                fieldlabel=""
                checkboxLabel="No Barcode"
                errMsg=""
                value={noBarcode}
                onChange={onChangeNoBarcode}
            />
        </div>
    );
};

export default DdsFieldsGroup;

// ---------------------------------------------------------------------------
// Row mapper (Quick Entry). Mirrors mapCaseNumberToFieldGroupRow's shape.
// ---------------------------------------------------------------------------
/**
 * Builds the FieldGroupRow for the DDS Information row.
 *
 * Required-ness replicates Classic's isBarcodeChecked quirk BIT-FOR-BIT
 * (localValidations.ts:233): when "No Barcode" has never been touched
 * (undefined) OR is checked, the six barcode fields carry NO required pressure -
 * so a fresh DDS transaction shows the row as EmptyNotRequired until the
 * specialist explicitly toggles the checkbox at least once. Only when No Barcode
 * is explicitly unchecked (false) is each of the six fields required.
 *
 * `aiValue` is accepted for signature parity with the other row mappers; the DDS
 * fields are barcode-sourced, not AI-extracted, so it is unused today.
 */
export function mapDdsInfoToFieldGroupRow(
    badgeNumber: number,
    formValues: Record<string, any>,
    _aiValue?: string,
): FieldGroupRow {
    const noBarcode = formValues[RequestorFormConstants.NO_BARCODE];
    const anyFilled = DDS_BARCODE_FIELDS.some(field => Boolean(formValues[field]));
    const allFilled = DDS_BARCODE_FIELDS.every(field => Boolean(formValues[field]));
    // isBarcodeChecked quirk: skip required checks when untouched OR checked. redux-form
    // seeds an unregistered/never-touched checkbox field to "" (empty string), never
    // undefined - a strict `=== undefined` check missed that, so a fresh DDS transaction
    // fell straight into the "explicitly unchecked, required" branch below and rode on
    // whichever of the six fields happened to be filled at that exact render (they land
    // one writeFieldToForm call at a time), flickering the row between NeedsEntry and
    // Confirmed while the scan was still writing. onChangeNoBarcode only ever writes a
    // real boolean, so `false` unambiguously means "explicitly unchecked."
    const untouched = noBarcode === undefined || noBarcode === "";
    const skipRequired = untouched || Boolean(noBarcode);

    let status: FieldGroupRowStatus;
    if (skipRequired) {
        // "No Barcode" checked, or a value was entered anyway => nothing outstanding.
        status = noBarcode === true || anyFilled
            ? FieldGroupRowStatus.Confirmed
            : FieldGroupRowStatus.EmptyNotRequired;
    } else {
        // "No Barcode" explicitly unchecked => each of the six fields is required.
        status = allFilled ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry;
    }

    // Never surface a DDS value (PHI-adjacent) as the read-only row text.
    let value: string | null = null;
    if (noBarcode === true) {
        value = "No barcode";
    } else if (status === FieldGroupRowStatus.Confirmed) {
        value = "DDS information entered";
    }

    return {
        id: DDS_INFO_ROW_ID,
        badgeNumber,
        status,
        label: "DDS Information",
        value,
        isAiPreSelected: false,
    };
}

/**
 * Spec #35136 (F1f), L1: fold DDS barcode completeness INTO the Requestor Info
 * row's status. For a DDS (Disability/Social Security) transaction the six barcode
 * fields render inside the Requestor row and carry AC-2 conditional-required
 * pressure. When mapDdsInfoToFieldGroupRow reports NeedsEntry (No Barcode unchecked
 * AND not all six filled) the whole Requestor row must read NeedsEntry, so the DDS
 * fields count as outstanding and block Confirm-All (getQuickEntryOutstandingCount ->
 * isQuickEntryConfirmAllEnabled). Before this the DDS fields could be left blank and
 * still pass Confirm-All. Non-DDS, or DDS Confirmed/EmptyNotRequired (all filled, or
 * No Barcode checked, or untouched-so-not-yet-required), returns the row unchanged.
 */
export function applyDdsCompletenessToRequestorRow(
    requestorRow: FieldGroupRow,
    requestType: number | undefined,
    formValues: Record<string, any>,
): FieldGroupRow {
    if (requestType !== DISABILITY_OR_SOCIAL_SECURITY) {
        return requestorRow;
    }
    return mapDdsInfoToFieldGroupRow(0, formValues).status === FieldGroupRowStatus.NeedsEntry
        ? { ...requestorRow, status: FieldGroupRowStatus.NeedsEntry }
        : requestorRow;
}

/**
 * Spec #35136 (F1f): the RQ Site Code the Quick Entry Requestor row's automated
 * search should key on, or undefined when it must NOT auto-search. Undefined unless
 * this is a DDS transaction with no already-linked requestor AND "No Barcode" is NOT
 * checked. The No-Barcode clause is a belt-and-suspenders guard alongside the
 * RequestorSection repopulate fix: a checked "No Barcode" means the specialist
 * deliberately cleared the barcode data, so the site-code search must not re-arm even
 * if a stale RQ Site Code lingers in the form. Non-DDS returns undefined (untouched).
 */
export function resolveDdsSiteCode(
    requestType: number | undefined,
    hasLinkedRequestor: boolean,
    formValues: Record<string, any>,
): string | undefined {
    if (requestType !== DISABILITY_OR_SOCIAL_SECURITY || hasLinkedRequestor) {
        return undefined;
    }
    if (formValues[RequestorFormConstants.NO_BARCODE]) {
        return undefined;
    }
    return formValues[RequestorFormConstants.RQ_SITE_CODE] || undefined;
}
